import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonImages from "@base/assets/images";
import CommonStyles from "@components/CommonStyles";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import { ILifeInsurance } from "@base/modules/patientInsurance/lifeInsurance.interface";
import { IHealthInsuranceCard } from "@base/modules/patientInsurance/healthInsurance.interface";
import patientInsuranceService from "@base/modules/patientInsurance/patientInsuranceService";
import HealthInsuranceCard from "./HealthInsuranceCard";
import LifeInsuranceItem from "./LifeInsuranceItem";
import InsuranceDetailModal from "./InsuranceDetailModal";

interface IProps {
  patientRecord: IPatientRecord;
}

/** Read-only, like the medical-profile tab on mobile - editing insurance lives elsewhere there. */
export default function InsuranceCards({ patientRecord }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const patientRecordCode = patientRecord.code;
  // The BHYT card comes back localised, so it is fetched again when the language changes.
  const lang = i18n.resolvedLanguage ?? "vi";

  const [healthCard, setHealthCard] = useState<IHealthInsuranceCard>();
  const [insurances, setInsurances] = useState<ILifeInsurance[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasFailed, setHasFailed] = useState<boolean>(false);
  const [selected, setSelected] = useState<ILifeInsurance>();

  //! function
  const getInsurances = useCallback(async () => {
    if (!patientRecordCode) {
      return;
    }

    setIsLoading(true);
    setHasFailed(false);

    const [healthResult, lifeResult] = await Promise.allSettled([
      patientInsuranceService.getHealthByPatientCode(patientRecordCode, lang),
      patientInsuranceService.getLifeByPatientCode(patientRecordCode),
    ]);

    // Like mobile, a BHYT card that is missing or fails to load just leaves its block out.
    const card = healthResult.status === "fulfilled" ? healthResult.value.data : undefined;
    setHealthCard(card?.name ? card : undefined);

    setInsurances(lifeResult.status === "fulfilled" ? (lifeResult.value.data ?? []) : []);
    setHasFailed(lifeResult.status === "rejected");

    setIsLoading(false);
  }, [patientRecordCode, lang]);

  useEffect(() => {
    getInsurances();
  }, [getInsurances]);

  //! render
  return (
    <div className="min-h-[442px] relative">
      <CommonStyles.SpinnerUI isSpinning={isLoading} />

      {healthCard || insurances.length > 0 ? (
        <div className="flex flex-col gap-4">
          {healthCard ? <HealthInsuranceCard card={healthCard} /> : null}

          {insurances.map((insurance) => (
            <LifeInsuranceItem key={insurance.id} insurance={insurance} patientRecord={patientRecord} onViewDetail={() => setSelected(insurance)} />
          ))}
        </div>
      ) : !isLoading ? (
        <div className="w-full min-h-[442px] flex flex-col justify-center items-center gap-6">
          <img src={CommonImages.EmptyRecordList} alt="" />

          <div className="text-common-maintext text-base font-semibold font-['Inter']">
            {hasFailed ? t("user.profile.insurance.loadFailed") : t("user.profile.insurance.empty")}
          </div>

          {hasFailed ? <CommonStyles.ButtonUI onClick={getInsurances}>{t("user.profile.insurance.retry")}</CommonStyles.ButtonUI> : null}
        </div>
      ) : null}

      <InsuranceDetailModal insurance={selected} onClose={() => setSelected(undefined)} />
    </div>
  );
}
